<template>
  <FormModal
    :visible="visible"
    :title="
      pessoaEdit
        ? 'Editar responsável pelo acesso da conta PJ'
        : 'Responsável pelo acesso da conta PJ'
    "
    :icon="require('@/assets/addRepresentante.png')"
    :loading="loading"
    confirm-label="Salvar"
    @update:visible="$emit('close')"
    @confirm="salvar"
    @cancel="$emit('close')"
  >
    <TabContainer
      :tabs="tabsVisiveis"
      :default-tab="abaAtiva"
      @tab-changed="abaAtiva = $event"
    >
      <!-- ABA: JÁ INFORMADO -->
      <template #jaInformado>
        <div class="form-subsection">
          <h4 class="form-subsection-title">
            <i class="fa fa-users"></i>
            Selecione o responsável
          </h4>

          <p class="form-field-description">
            Defina quem será a pessoa solicitada em futuras validações de
            identidade e envio de documentos. Lembre-se: o e-mail de acesso à
            conta Genial é o e-mail da empresa, informado no cadastro.
          </p>

          <div class="form-grid-1">
            <div class="form-field">
              <label class="form-field-label form-field-label-required">
                Sócios/Acionistas, Procuradores, Responsável legal e Emissor de
                Ordem
              </label>
              <ModalSelect
                class="field"
                v-model="pessoaSelecionada"
                @change="habilitarSalvar"
                :options="[
                  { Guid: '', Nome: 'Selecione' },
                  ...(pessoasFiltradas || []),
                ]"
                value-key="Guid"
                label-key="Nome"
                :custom-label="
                  (pessoa) =>
                    pessoa.Guid === ''
                      ? pessoa.Nome
                      : pessoa.Nome + ' - ' + formatarCpf(pessoa.CpfCnpj)
                "
                title="Selecionar pessoa"
                placeholder="Selecione"
              />
              <p v-if="erros.pessoaSelecionada" class="form-field-error">
                Selecione uma pessoa
              </p>
            </div>
          </div>

          <div v-if="pessoaSelecionada" class="info-box warning">
            <i class="fa fa-exclamation-triangle"></i>
            <strong>Atenção:</strong> Somente uma pessoa pode ser o responsável
            pelo acesso da conta PJ.
          </div>
        </div>
      </template>

      <!-- ABA: NOVO -->
      <template #novo>
        <div class="form-subsection">
          <h4 class="form-subsection-title">
            <i class="fa fa-user-plus"></i>
            Novo responsável
          </h4>

          <p class="form-field-description">
            Caso o responsável pelo acesso não esteja listado anteriormente,
            você pode cadastrá-lo abaixo.
          </p>

          <div class="form-grid-1">
            <div class="form-field">
              <label class="form-field-label form-field-label-required"
                >Nome completo</label
              >
              <input
                type="text"
                class="field"
                autocomplete="off"
                v-model="novaPessoa.Nome"
                placeholder="Digite o nome completo"
              />
              <p v-if="erros.Nome" class="form-field-error">
                Campo obrigatório
              </p>
            </div>
          </div>

          <div class="form-grid-1">
            <div class="form-field">
              <label class="form-field-label form-field-label-required"
                >E-mail</label
              >
              <input
                type="email"
                class="field"
                autocomplete="off"
                v-model="novaPessoa.Email"
                placeholder="exemplo@email.com"
                @blur="validarEmail"
              />
              <p v-if="erros.Email" class="form-field-error">
                {{ erros.Email }}
              </p>
            </div>
          </div>

          <div class="form-grid-1">
            <div class="form-field">
              <label class="form-field-label form-field-label-required"
                >CPF</label
              >
              <input
                type="text"
                class="field"
                autocomplete="off"
                v-model="novaPessoa.CpfCnpj"
                v-mask="'###.###.###-##'"
                placeholder="000.000.000-00"
                @blur="validarCpf"
              />
              <p v-if="erros.CpfCnpj" class="form-field-error">
                Campo obrigatório
              </p>
              <p v-if="erroCpf" class="form-field-error">
                {{ mensagemErroCpf }}
              </p>
            </div>
          </div>

          <div class="info-box">
            <i class="fa fa-info-circle"></i>
            O responsável será utilizado para validações de identidade e envio
            de documentos.
          </div>
        </div>
      </template>
    </TabContainer>
  </FormModal>
</template>

<script>
import ModalSelect from "@/components/ModalSelect.vue";
import { mask } from "vue-the-mask";
import FormModal from "@/components/FormModal.vue";
import TabContainer from "@/components/TabContainer.vue";
import documentoUtils, { isCNPJ } from "@/utils/documentoUtils";
import emailValidatorMixin from "@/utils/emailValidatorMixin.js";

export default {
  name: "ModalResponsavelConta",
  components: {
    ModalSelect,
    FormModal,
    TabContainer,
  },
  directives: {
    mask,
  },
  mixins: [emailValidatorMixin],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    todasPessoas: {
      type: Array,
      default: () => [],
    },
    pessoaEdit: {
      type: Object,
      default: null,
    },
  },
  computed: {
    tabsVisiveis() {
      if (this.pessoaEdit) {
        return this.tabs.filter((tab) => tab.key === "novo");
      }
      return this.tabs;
    },
    pessoasFiltradas() {
      return (this.todasPessoas || []).filter(
        (pessoa) => pessoa.TipoDocumento == "CPF",
      );
    },
  },
  data() {
    return {
      loading: false,
      abaAtiva: "jaInformado",
      tabs: [
        { key: "jaInformado", label: "JÁ INFORMADO" },
        { key: "novo", label: "NOVO" },
      ],
      pessoaSelecionada: "",
      erros: {},
      erroCpf: false,
      mensagemErroCpf: "",
      novaPessoa: {
        Guid: "",
        Nome: "",
        Email: "",
        CpfCnpj: "",
        TipoDocumento: "CPF",
        Tipo: "Responsável pela conta",
      },
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.pessoaEdit) {
            this.abaAtiva = "novo";
            this.novaPessoa = {
              ...this.novaPessoa,
              Guid: this.pessoaEdit.Guid || "",
              Nome: this.pessoaEdit.Nome || "",
              Email: this.pessoaEdit.Email || "",
              CpfCnpj: this.pessoaEdit.CpfCnpj || "",
              TipoDocumento: this.pessoaEdit.TipoDocumento || "CPF",
            };
            if (
              isCNPJ(this.novaPessoa.CpfCnpj) ||
              this.novaPessoa.TipoDocumento === "CNPJ"
            ) {
              this.novaPessoa.TipoDocumento = "";
              this.novaPessoa.CpfCnpj = "";
            }
          } else {
            this.limpar();
          }
        });
      } else {
        this.limpar();
      }
    },
  },
  methods: {
    formatarCpf(cpf) {
      if (!cpf) return "";
      const numeros = cpf.replace(/\D/g, "");
      if (numeros.length === 11) {
        return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      return cpf;
    },
    habilitarSalvar() {
      this.erros.pessoaSelecionada = false;
    },
    validarEmail() {
      this.validarEmailGeneric(this.novaPessoa.Email, "Email", {
        obrigatorio: true,
      });
    },
    validarCpf() {
      if (this.novaPessoa.CpfCnpj) {
        const resultado = documentoUtils.validarDocumentoCompleto(
          this.novaPessoa.CpfCnpj,
          "CPF",
        );
        this.erroCpf = !resultado.valido;
        this.mensagemErroCpf = resultado.mensagem;
      }
    },
    validarJaInformado() {
      this.erros = {};
      if (!this.pessoaSelecionada) {
        this.erros.pessoaSelecionada = true;
        return false;
      }
      return true;
    },
    validarNovo() {
      this.erros = {};
      let valido = true;

      if (!this.novaPessoa.Nome || this.novaPessoa.Nome.trim() === "") {
        this.erros.Nome = true;
        valido = false;
      }

      if (
        !this.novaPessoa.Email ||
        Object.values(this.emailErros || {}).some((e) => e)
      ) {
        this.erros.Email = this.emailErros?.Email || "Campo obrigatório";
        valido = false;
      }

      if (!this.novaPessoa.CpfCnpj || this.erroCpf) {
        this.erros.CpfCnpj = true;
        valido = false;
      }

      return valido;
    },
    async salvar() {
      if (
        this.pessoaEdit &&
        (isCNPJ(this.novaPessoa.CpfCnpj) ||
          this.novaPessoa.TipoDocumento === "CNPJ")
      ) {
        this.$alertWarning(
          "Documento inválido",
          "O documento CNPJ não é permitido para o responsável pelo login. Informe um CPF válido antes de salvar.",
        );
        return;
      }
      if (this.abaAtiva === "jaInformado") {
        if (!this.validarJaInformado()) {
          this.$swal({
            icon: "warning",
            title: "Atenção",
            text: "Selecione uma pessoa para ser o responsável pelo acesso da conta PJ.",
          });
          return;
        }

        this.loading = true;
        this.$emit("submit", {
          guid: this.pessoaSelecionada,
          jaInformado: true,
        });
      } else {
        if (!this.validarNovo()) {
          this.$swal({
            icon: "warning",
            title: "Atenção",
            text: "Preencha todos os campos obrigatórios.",
          });
          return;
        }

        this.loading = true;
        this.$emit("submit", {
          ...this.novaPessoa,
          jaInformado: false,
        });
      }
    },
    resetarLoading() {
      this.loading = false;
    },
    limpar() {
      this.abaAtiva = "jaInformado";
      this.pessoaSelecionada = "";
      this.novaPessoa = {
        Guid: "",
        Nome: "",
        Email: "",
        CpfCnpj: "",
        TipoDocumento: "CPF",
        Tipo: "Responsável pela conta",
      };
      this.erros = {};
      this.erroCpf = false;
      this.loading = false;
      this.mensagemErroCpf = "";
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.form-field-description {
  font-size: 0.875rem;
  line-height: 1.5;
  color: #6b7280;
  margin-bottom: 1.25rem;

  :global(.dark) & {
    color: #9ca3af;
  }
}

.info-box {
  margin-top: 1rem;
  padding: 0.875rem 1rem;
  background-color: #eff6ff;
  border-left: 4px solid #3b82f6;
  border-radius: 0.375rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.875rem;

  i {
    color: #3b82f6;
    font-size: 1.125rem;
    margin-top: 0.125rem;
    flex-shrink: 0;
  }

  &.warning {
    background-color: #fef3c7;
    border-left-color: #f59e0b;

    i {
      color: #f59e0b;
    }
  }

  strong {
    font-weight: 600;
  }
}
</style>
